import { isRecordFieldValueComplete, parseRecordFieldReference } from "./deltaOutputFields";
import { templateReadsColumn } from "./extensionTemplateColumns";
import { resolveAdapterPathSelectors } from "./pathSelectors";
import { matchesAdapterAnswers } from "./helpers";
import type { AdapterGuidance, AdapterApplyAnswers, AdapterMappingGuidanceRequirement } from "./types";

const pathSegmentPattern = /([^[.\]]+)|\[(\d+)\]/g;

export const getPathValue = (source: unknown, path: string): unknown => {
  const segments = Array.from(path.matchAll(pathSegmentPattern)).map((match) => match[1] ?? match[2]);
  let current = source;

  for (const segment of segments) {
    if (Array.isArray(current)) {
      current = current[Number(segment)];
    } else if (current && typeof current === "object") {
      current = (current as Record<string, unknown>)[segment];
    } else {
      return undefined;
    }
  }

  return current;
};

const isBlankValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim().length === 0) ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0);

/** A comma-separated date format list, as a column's `dateFormat` holds it. */
export const splitDateFormats = (value: unknown): string[] =>
  typeof value === "string" ? value.split(",").map((format) => format.trim()).filter(Boolean) : [];

/** What requirement checks may know about the mapping being edited, beyond its values. */
export interface RequirementContext {
  /** The fields available on a delta mapping's `record`, when known; `recordField` checks references against them. */
  recordFields?: string[];
}

/** Blank, or an unfilled `record['']` placeholder: an optional guided field in this state has nothing to check. */
export const isUnfilledValue = (value: unknown): boolean =>
  isBlankValue(value) || parseRecordFieldReference(value)?.trim() === "";

export const isRequirementComplete = (
  source: unknown,
  requirement: AdapterMappingGuidanceRequirement,
  guidanceId?: string,
  context: RequirementContext = {},
): boolean => {
  const path = resolveAdapterPathSelectors(
    source,
    requirement.path,
    guidanceId ? { id: guidanceId } : {},
  ) ?? requirement.path;
  const value = getPathValue(source, path);
  if (requirement.operator === "notBlank") return !isBlankValue(value);
  if (requirement.operator === "recordField") return isRecordFieldValueComplete(value, context.recordFields);
  if (requirement.operator === "templateColumn") return templateReadsColumn(value, String(requirement.value ?? ""));
  if (requirement.operator === "dateFormats") {
    const formats = new Set(splitDateFormats(value));
    return splitDateFormats(requirement.value).every((format) => formats.has(format));
  }
  if (requirement.value === "") return isBlankValue(value);
  return value === requirement.value;
};

export const getResourceGuidance = (guidance: AdapterGuidance[] = [], answers: AdapterApplyAnswers): AdapterGuidance[] =>
  guidance.filter((item) => matchesAdapterAnswers(answers, item.showWhen));

export const isGuidanceComplete = (values: unknown, item: AdapterGuidance, context: RequirementContext = {}): boolean =>
  (Boolean(item.optional) && isUnfilledValue(getPathValue(values, resolveAdapterPathSelectors(values, item.targetPath, { id: item.id }) ?? item.targetPath))) ||
  item.requirements.every((requirement) => isRequirementComplete(values, requirement, item.id, context));
